import styled from "styled-components";

import { useState, useRef, useEffect, } from "react";
import { createPortal, } from "react-dom";
import { getTheme, themed, Div, Text as Tx, } from "@bracket/ui";
import type React from "react";



type DropdownProps<T> = {
	items: T[];
	selectedItem: T;
	onSelect: (item: T) => void;
	renderItem: (item: T, disabled?:boolean) => React.ReactNode;
	renderSelectedItem?: (item?: T) => React.ReactNode;
	disabledItems: T[];
	disabled?: boolean;
	label?: string;
	margin?: string;
	buttonWidth?: string;
};

function Dropdown<T>({
	items,
	selectedItem,
	onSelect,
	renderItem,
	renderSelectedItem = (item: T) => item ? item.toString() : "no selection",
	disabledItems = [],
	disabled,
	label,
	margin,
	buttonWidth,
}: DropdownProps<T>) {
	themed();


	const [isOpen, setIsOpen] = useState(false);
	const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
	const buttonRef = useRef<HTMLButtonElement>(null);
	const dropdownRef = useRef<HTMLUListElement>(null);

	const handleToggleDropdown = () => {
		if(items.length <= 1) return;
		if(!isOpen && buttonRef.current) {
			const rect = buttonRef.current.getBoundingClientRect();
			setDropdownPosition({
				top: rect.bottom + window.scrollY,
				left: rect.left + window.scrollX,
			});
		}
		setIsOpen(!isOpen);
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				buttonRef.current &&
				!buttonRef.current.contains(event.target as Node) &&
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	return (
		<DropdownContainer $margin={margin}>
			{label && <Tx md as="span" color="primary">{label}</Tx>}
			<DropdownButton
				ref={buttonRef}
				onClick={handleToggleDropdown}
				$disabled={items.length <= 1}
				$buttonWidth={buttonWidth}
			>
				{renderSelectedItem(selectedItem)}
				{items.length > 1 && (isOpen ? <ChevronUp /> : <ChevronDown />)}
			</DropdownButton>
			{isOpen &&
				createPortal(
					<DropdownList
						ref={dropdownRef}
						$top={dropdownPosition.top}
						$left={dropdownPosition.left}
						$buttonWidth={buttonWidth}
					>
						{items.map((item, index) => (
							<DropdownItem
							key={(item as any).symbol || (item as any).value || index}
								onClick={() => {
									onSelect(item);
									setIsOpen(false);
								}}
							>
								{renderItem(item, disabledItems.includes(item))}
							</DropdownItem>
						))}
					</DropdownList>,
					document.body
				)}
		</DropdownContainer>
	);
}



const DropdownContainer = styled(Div)<{ $margin?: string }>`
	position: relative;
	margin-left: ${(props) => props.$margin ? `${props.$margin}` : "1rem"};
	z-index: 0;
	border-radius: 8px;
	box-shadow: 0 0 10px  #00000023;
	mix-blend-mode: lighten-only;
	&::after {
		content: "";
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: url(${getTheme().assets.TX_GRAIN_ONE}) no-repeat center center;
		border-radius: 8px;
		background-size: 300% 300%;
		opacity: 0.45;
		z-index: -1;
	}
`;


const DropdownButton = styled.button<{ $disabled: boolean; $buttonWidth?: string; }>`
	display: flex;
	align-items: center;
	padding: 1rem;
	background-color: ${() => getTheme().surface_container_high};
	border: none;
	border-radius: 8px;
	color: ${() => getTheme().surface_on};
	font-size: 1rem;
	cursor: ${(props) => (props.$disabled ? "default" : "pointer")};
	width: ${(props) => props.$buttonWidth ? `${props.$buttonWidth}` : "140px"};
`;

const DropdownList = styled.ul<{ $top: number; $left: number; $buttonWidth?: string; }>`
	position: absolute;
	top: ${(props) => props.$top}px;
	left: ${(props) => props.$left}px;
	width: ${(props) => props.$buttonWidth ? `${props.$buttonWidth}` : "140px"};
	background-color: ${() => getTheme().surface_container_high};
	border-radius: 0px 0px 8px 8px;
	list-style-type: none;
	padding: 0 0 8px 4px;
	margin: -3px 0 0;
	max-height: 250px;
	overflow-y: auto;
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
	z-index: 1000;

	&::-webkit-scrollbar {
		width: 10px;
	}

	&::-webkit-scrollbar-track {
		// background: ${() => getTheme().error};
		border-radius: 8px;
	}

	&::-webkit-scrollbar-thumb {
		// background-color: ${() => getTheme().error};
		border-radius: 8px;
		// border: 2px solid ${() => getTheme().error};
	}

	scrollbar-width: thin;
	scrollbar-color: ${() => getTheme().primary} ${() => getTheme().surface_container_high};
`;

const DropdownItem = styled.li`
	display: flex;
	align-items: center;
	padding: 8px 12px;
	cursor: pointer;
	color: ${() => getTheme().paragraph};
	&:hover {
		background-color: ${() => getTheme().surface_container_high};
	}
`;

const ChevronIcon = styled.svg`
	width: 16px;
	height: 16px;
	margin-left: auto;
`;

const ChevronDown = () => (
	<ChevronIcon
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			d="M6 9L12 15L18 9"
			stroke={getTheme().paragraph}
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</ChevronIcon>
);

const ChevronUp = () => (
	<ChevronIcon
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			d="M18 15L12 9L6 15"
			stroke={getTheme().paragraph}
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</ChevronIcon>
);


export { Dropdown };

